import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismIcon, PrismInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import { MAX_LENGTH_KEY_DATE_NOTE } from 'lumen/utils/constants/overview-dashboard-constants';

interface HotelOverviewDashboardFeedKeyDatesNoteSignature {
  Args: {
    data: unknown;
    hasNote: unknown;
    isAlertsRequestLoading: unknown;
    isFeedEntryDayDrilledDown: unknown;
    relaunchAlertsDataRequest: unknown;
  };
}

export default class HotelOverviewDashboardFeedKeyDatesNote extends Component<HotelOverviewDashboardFeedKeyDatesNoteSignature> {
  @service declare alerting: Services['alerting'];

  @tracked isEditButtonClicked = false;
  // used for the textarea
  @tracked keyDateNote = '';
  @tracked keyDateNoteBeforeEdit = '';

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areEditButtonsDisabled() {
    // Disable both Cancel & save button when the alerts request is loading
    return this.args.isAlertsRequestLoading;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveButtonDisabled() {
    // Disable only the save button if the note hasn't been changed, or if the note is too long
    return (
      this.keyDateNote === this.keyDateNoteBeforeEdit ||
      (this.keyDateNote !== '' &&
        this.keyDateNote.length > MAX_LENGTH_KEY_DATE_NOTE)
    );
  }

  // The Edit state is shown in following two cases:
  // Case 1: The keyDate has a note and the edit button is clicked
  // Case 2: The keyDate doesn't have a note but that day is drilled down
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showEditState() {
    return (
      (this.args.hasNote && this.isEditButtonClicked) ||
      (!this.args.hasNote && this.args.isFeedEntryDayDrilledDown)
    );
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    if (this.args.data) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.keyDateNote = this.args.data.text;
    }
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onCancelClicked() {
    // Result of cancel button depends on the scenario. If there is no original note, it just clears the textarea
    if (!this.keyDateNoteBeforeEdit) {
      this.keyDateNote = '';
    } else {
      // If there is a previous note, we reset the note value & we return to the standard "display note" state
      this.keyDateNote = this.keyDateNoteBeforeEdit;

      this.isEditButtonClicked = false;
    }
  }

  @action
  async onDeleteClicked(): Promise<void> {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const result = await this.args.updateKeyDateNoteTask.perform(
      // @ts-expect-error: Incorrect type
      this.args.keyDateFromDate,
      '',
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (result.isSuccess) {
      // Currently we refresh the whole feed after a succesful update. This indirectly also handles the "state" change in this component.
      // @ts-expect-error: Incorrect type
      this.args.relaunchAlertsDataRequest();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
    }
  }

  @action
  onEditClicked(): void {
    this.isEditButtonClicked = !this.isEditButtonClicked;

    // Store the current note content in a variable, so that we can check if the note has been changed or not
    this.keyDateNoteBeforeEdit = this.keyDateNote;
  }

  @action
  async onSaveClicked(): Promise<void> {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const result = await this.args.updateKeyDateNoteTask.perform(
      // @ts-expect-error: Incorrect type
      this.args.keyDateFromDate,
      this.keyDateNote,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (result.isSuccess) {
      // Currently we refresh the whole feed after a succesful update. This indirectly also handles the "state" change in this component.
      // @ts-expect-error: Incorrect type
      this.args.relaunchAlertsDataRequest();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
    }
  }

  // @ts-expect-error: Incorrect type
  @action updateKeyDateNote(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.keyDateNote = event.target.value;
  }

  <template>
    {{#if this.showEditState}}
      <PrismInput
        @maxLength={{MAX_LENGTH_KEY_DATE_NOTE}}
        @onInput={{this.updateKeyDateNote}}
        @placeholder={{t
          "overview_dashboard.key_date.modal.input.reminder.placeholder"
        }}
        @removePadding={{true}}
        @rows={{5}}
        @textarea={{true}}
        @value={{this.keyDateNote}}
      />

      <div class="key-date-btn-wrapper mt-200">
        <PrismButton
          @buttonType="secondary"
          {{! @glint-expect-error: Incorrect type }}
          @isDisabled={{this.areEditButtonsDisabled}}
          @label={{t "general.cancel"}}
          @onClick={{this.onCancelClicked}}
        />
        <PrismButton
          {{! @glint-expect-error: Incorrect type }}
          @isDisabled={{or
            this.isSaveButtonDisabled
            this.areEditButtonsDisabled
          }}
          @label={{t "general.save"}}
          @onClick={{this.onSaveClicked}}
        />
      </div>
    {{else if this.keyDateNote}}
      <div class="od-feed-entry__note-section__note">
        <div class="mt-200 mb-100 flex flex-row items-center justify-between">
          <p class="text-300--bold">{{t
              "overview_dashboard.feed.note.title"
            }}</p>
          <div>
            <button
              class="button--unstyled"
              type="button"
              {{on "click" this.onEditClicked}}
            >
              <PrismIcon @name="edit-pencil" @size="small" />
            </button>
            <button
              class="button--unstyled ml-200"
              type="button"
              {{on "click" this.onDeleteClicked}}
            >
              <PrismIcon @name="bin" @size="small" />
            </button>
          </div>
        </div>

        <p class="paragraph-300--regular">{{this.keyDateNote}}</p>
      </div>
    {{/if}}
  </template>
}
